interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface StreamCallbacks {
  onStart?: () => void;
  onToken?: (token: string) => void;
  onDocuments?: (documents: DocumentResponse[]) => void;
  onFinish?: (sessionId?: string) => void;  // Changed to string for UUID
  onError?: (error: string) => void;
}

interface ChatRequest {
  session_id?: string;
  message: string;
  response_style?: 'concise' | 'normal' | 'descriptive';
  source_filter?: string[];
}

interface ChatSession {
  id: string;  // Changed to string for UUID
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

interface ChatMessage {
  id: number;
  role: string;
  content: string;
  documents?: DocumentResponse[];
  created_at: string;
}

interface ChatHistory {
  session: ChatSession;
  messages: ChatMessage[];
}

interface AuthResponse {
  access_token: string;
  token_type: string;
}

interface UserInfo {
  id: number;
  username: string;
  created_at: string;
  company_id?: number | null;
}

interface ProcessRequest {
  session_id?: number;
  message: string;
  categories?: string[];
  jurisdictions: string[];
  organic_mode: boolean;
}

interface DocumentResponse {
  document: string;
  category: string;
  stage?: string;
  step_order?: string;
  jurisdiction: string;
  date: string;
  content: string;
  headline: string;
  source_url: string;
  source: string;
  file?: string;  // For Example Policies documents
}

interface ProcessResponse {
  response: string;
  documents: DocumentResponse[];
  session_id: number;
}

class ChatAPI {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string = 'http://localhost:8000') {

    const envApiUrl = process.env.REACT_APP_API_BASE_URL;
    this.baseUrl = envApiUrl && envApiUrl.trim() !== '' ? envApiUrl : (baseUrl || 'http://localhost:8000');
    // Load token from localStorage
    this.token = localStorage.getItem('auth_token');
  }

  private getAuthHeaders() {
    if (!this.token) {
      throw new Error('No authentication token available');
    }
    return {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    };
  }

  async login(username: string, password: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const authData: AuthResponse = await response.json();
      this.token = authData.access_token;
      localStorage.setItem('auth_token', this.token);
      return authData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async register(username: string, password: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const authData: AuthResponse = await response.json();
      this.token = authData.access_token;
      localStorage.setItem('auth_token', this.token);
      return authData;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<UserInfo> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/me`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (!response.ok) {
        throw new Error('Failed to get user info');
      }

      return await response.json();
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  }

  logout() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  private handleUnauthorized() {
    // Clear the token
    this.logout();
    
    // Navigate to login page
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  async streamChat(
    sessionId: string | null, 
    message: string, 
    callbacks: StreamCallbacks = {},
    responseStyle: 'concise' | 'normal' | 'descriptive' = 'normal',
    sourceFilter: string[] = []
  ) {
    try {
      const requestBody: ChatRequest = {
        session_id: sessionId || undefined,
        message: message,
        response_style: responseStyle,
        source_filter: sourceFilter.length > 0 ? sourceFilter : undefined
      };

      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      callbacks.onStart?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            // Process any remaining data in buffer
            if (buffer.trim()) {
              this.processBuffer(buffer, callbacks);
            }
            break;
          }

          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete lines
          const lines = buffer.split('\n');
          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.trim() && line.startsWith('data: ')) {
              try {
                const jsonStr = line.slice(6).trim();
                if (jsonStr) {
                  const data = JSON.parse(jsonStr);
                  
                  if (data.type === 'token') {
                    callbacks.onToken?.(data.content);
                  } else if (data.type === 'documents') {
                    callbacks.onDocuments?.(data.documents);
                  } else if (data.type === 'done') {
                    callbacks.onFinish?.(data.session_id);
                    return;
                  } else if (data.type === 'error') {
                    callbacks.onError?.(data.content);
                    return;
                  }
                }
              } catch (e) {
                console.warn('Failed to parse SSE data:', line, e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

    } catch (error) {
      console.error('Stream chat error:', error);
      callbacks.onError?.(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  private processBuffer(buffer: string, callbacks: StreamCallbacks) {
    const lines = buffer.split('\n');
    for (const line of lines) {
      if (line.trim() && line.startsWith('data: ')) {
        try {
          const jsonStr = line.slice(6).trim();
          if (jsonStr) {
            const data = JSON.parse(jsonStr);
            
            if (data.type === 'token') {
              callbacks.onToken?.(data.content);
            } else if (data.type === 'done') {
              callbacks.onFinish?.(data.session_id);
            } else if (data.type === 'error') {
              callbacks.onError?.(data.content);
            }
          }
        } catch (e) {
          console.warn('Failed to parse buffered SSE data:', line, e);
        }
      }
    }
  }

  async getChatSessions(): Promise<ChatSession[]> {
    try {
      const response = await fetch(`${this.baseUrl}/chat/sessions`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (!response.ok) {
        throw new Error('Failed to get chat sessions');
      }

      return await response.json();
    } catch (error) {
      console.error('Get chat sessions error:', error);
      throw error;
    }
  }

  async getChatHistory(sessionId: string): Promise<ChatHistory> {
    try {
      const response = await fetch(`${this.baseUrl}/chat/sessions/${sessionId}`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (!response.ok) {
        throw new Error('Failed to get chat history');
      }

      return await response.json();
    } catch (error) {
      console.error('Get chat history error:', error);
      throw error;
    }
  }

  async deleteChatSession(sessionId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/chat/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (!response.ok) {
        throw new Error('Failed to delete chat session');
      }
    } catch (error) {
      console.error('Delete chat session error:', error);
      throw error;
    }
  }

  async healthCheck(): Promise<{ status: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return await response.json();
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }

  // Admin methods
  async getConstitution(): Promise<ConstitutionResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/constitution`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to get constitution');
      }

      return await response.json();
    } catch (error) {
      console.error('Get constitution error:', error);
      throw error;
    }
  }

  async updateConstitution(constitution: string): Promise<{ message: string; updated_by: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/constitution`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ constitution })
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to update constitution');
      }

      return await response.json();
    } catch (error) {
      console.error('Update constitution error:', error);
      throw error;
    }
  }

  async getAllUsersMetrics(): Promise<UserMetrics[]> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/users`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to get user metrics');
      }

      return await response.json();
    } catch (error) {
      console.error('Get user metrics error:', error);
      throw error;
    }
  }

  async getUserSessions(userId: number): Promise<SessionMetrics[]> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/users/${userId}/sessions`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to get user sessions');
      }

      return await response.json();
    } catch (error) {
      console.error('Get user sessions error:', error);
      throw error;
    }
  }

  async getSessionDetails(sessionId: string): Promise<ChatHistory> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/sessions/${sessionId}`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to get session details');
      }

      return await response.json();
    } catch (error) {
      console.error('Get session details error:', error);
      throw error;
    }
  }

  async getConstitutionHistory(): Promise<ConstitutionHistory[]> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/constitution/history`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to get constitution history');
      }

      return await response.json();
    } catch (error) {
      console.error('Get constitution history error:', error);
      throw error;
    }
  }

  async restoreConstitution(historyId: string): Promise<{ message: string; updated_by: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/constitution/restore/${historyId}`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to restore constitution');
      }

      return await response.json();
    } catch (error) {
      console.error('Restore constitution error:', error);
      throw error;
    }
  }

  // Company management methods
  async getAllCompanies(): Promise<CompanyResponse[]> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/companies`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to get companies');
      }

      return await response.json();
    } catch (error) {
      console.error('Get companies error:', error);
      throw error;
    }
  }

  async createCompany(company: CompanyCreateRequest): Promise<CompanyResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/companies`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(company)
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create company');
      }

      return await response.json();
    } catch (error) {
      console.error('Create company error:', error);
      throw error;
    }
  }

  async getCompany(companyId: number): Promise<CompanyResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/companies/${companyId}`, {
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        throw new Error('Failed to get company');
      }

      return await response.json();
    } catch (error) {
      console.error('Get company error:', error);
      throw error;
    }
  }

  async updateCompany(companyId: number, updates: CompanyUpdateRequest): Promise<CompanyResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/companies/${companyId}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates)
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update company');
      }

      return await response.json();
    } catch (error) {
      console.error('Update company error:', error);
      throw error;
    }
  }

  async deleteCompany(companyId: number): Promise<{ message: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/admin/companies/${companyId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete company');
      }

      return await response.json();
    } catch (error) {
      console.error('Delete company error:', error);
      throw error;
    }
  }

  async uploadDocuments(companyId: number, files: FileList): Promise<{ message: string; company_name: string; uploaded_files: string[]; statistics: any }> {
    try {
      const formData = new FormData();
      
      // Add all files to form data
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }

      const response = await fetch(`${this.baseUrl}/admin/upload-documents/${companyId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
        },
        body: formData
      });

      if (response.status === 401) {
        this.handleUnauthorized();
        throw new Error('Authentication failed');
      }

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload documents');
      }

      return await response.json();
    } catch (error) {
      console.error('Upload documents error:', error);
      throw error;
    }
  }
}

// Create singleton instance
export const chatAPI = new ChatAPI();

export default chatAPI;
// Admin interfaces
interface ConstitutionResponse {
  constitution: string;
  last_updated: string;
  updated_by: string;
}

interface ConstitutionUpdateRequest {
  constitution: string;
}

interface ConstitutionHistory {
  id: string;
  constitution: string;
  created_at: string;
  updated_by: string;
  is_current: boolean;
}

interface UserMetrics {
  id: number;
  username: string;
  created_at: string;
  is_admin?: string;
  total_sessions: number;
  total_messages: number;
  last_activity?: string;
}

interface SessionMetrics {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  user_id: number;
}

interface CompanyResponse {
  id: number;
  name: string;
  mongo_collection_name: string;
  pinecone_collection_name: string;
  color_scheme: string;
  created_at: string;
  user_count: number;
}

interface CompanyCreateRequest {
  name: string;
  mongo_collection_name: string;
  pinecone_collection_name: string;
  color_scheme?: string;
}

interface CompanyUpdateRequest {
  name?: string;
  mongo_collection_name?: string;
  pinecone_collection_name?: string;
  color_scheme?: string;
}

export type { 
  ChatSession, 
  ChatMessage, 
  ChatHistory, 
  UserInfo, 
  AuthResponse, 
  ProcessRequest, 
  ProcessResponse, 
  DocumentResponse,
  ConstitutionResponse,
  ConstitutionUpdateRequest,
  ConstitutionHistory,
  UserMetrics,
  SessionMetrics,
  CompanyResponse,
  CompanyCreateRequest,
  CompanyUpdateRequest
};
